import { getCurrentScope, onScopeDispose, shallowReactive } from "vue";

/** Returns true while the page holds something a reload would throw away (unsaved edits, a playing video). */
export type ReloadBlocker = () => boolean;

/**
 * Page-wide registry the service-worker `controllerchange` reload consults before reloading a tab the member did not
 * ask to reload (PwaUpdatePrompt.vue). A feature registers a blocker from its own file; the guard is never edited per
 * feature. It never applies to the IndexedDB `versionchange` reload: that connection is already closed.
 *
 * The set is reactive and a blocker that reads refs is tracked through `isReloadBlocked()`, so a deferred reload
 * resumes on its own once the last blocker clears or unregisters.
 */
const blockers = shallowReactive( new Set< { blocks: ReloadBlocker } >() );

/** @returns the unregister function; calling it more than once is harmless. */
export function registerReloadBlocker( blocks: ReloadBlocker ): () => void {
    // One entry per registration, so the same function registered twice unregisters independently.
    const entry = { blocks };
    blockers.add( entry );
    return () => { blockers.delete( entry ); };
}

export function isReloadBlocked(): boolean {
    for ( const { blocks } of blockers ) {
        try {
            if ( blocks() ) {
                return true;
            }
        } catch {
            // A broken blocker must not pin the tab to an old bundle forever.
        }
    }
    return false;
}

/** `registerReloadBlocker` tied to the current component or effect scope: it unregisters when that scope ends. */
export function useReloadBlocker( blocks: ReloadBlocker ): () => void {
    const unregister = registerReloadBlocker( blocks );
    if ( getCurrentScope() ) {
        onScopeDispose( unregister );
    }
    return unregister;
}
